import React, { useState, useEffect } from 'react';
import { matchesAPI, teamsAPI } from '../../services/api';
import { FaExclamationTriangle, FaHistory } from 'react-icons/fa';
import { useLanguage } from '../../context/LanguageContext';

const DisciplineSection = () => {
  const { language, t } = useLanguage();
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [searchTeam, setSearchTeam] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [matchesRes, teamsRes] = await Promise.all([
        matchesAPI.getAll(),
        teamsAPI.getAll()
      ]);
      setMatches(matchesRes.data || []);
      setTeams(teamsRes.data || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculer les cartons par équipe
  const calculateTeamCards = () => {
    const teamCards = {};

    teams.forEach(team => {
      teamCards[team.id] = {
        team: team,
        yellowCards: 0,
        redCards: 0,
        totalYellow: 0,
        totalRed: 0,
        suspensions: 0
      };
    });

    // Compter les cartons des matchs terminés
    matches
      .filter(m => m.status === 'finished')
      .forEach(match => {
        if (match.home_team_id && teamCards[match.home_team_id]) {
          teamCards[match.home_team_id].yellowCards += (match.yellow_cards_home || 0);
          teamCards[match.home_team_id].redCards += (match.red_cards_home || 0);
          teamCards[match.home_team_id].totalYellow += (match.yellow_cards_home || 0);
          teamCards[match.home_team_id].totalRed += (match.red_cards_home || 0);
        }
        if (match.away_team_id && teamCards[match.away_team_id]) {
          teamCards[match.away_team_id].yellowCards += (match.yellow_cards_away || 0);
          teamCards[match.away_team_id].redCards += (match.red_cards_away || 0);
          teamCards[match.away_team_id].totalYellow += (match.yellow_cards_away || 0);
          teamCards[match.away_team_id].totalRed += (match.red_cards_away || 0);
        }
      });

    // Calculer les suspensions (3 jaunes = 1 suspension, 1 rouge = 1 suspension)
    Object.values(teamCards).forEach(data => {
      data.suspensions = Math.floor(data.yellowCards / 3) + data.redCards;
      data.yellowCards = data.yellowCards % 3; // Cartons restants après suspensions
    });

    return Object.values(teamCards);
  };

  const teamCardsData = calculateTeamCards();

  // Filtrer par groupe et recherche
  const filteredData = teamCardsData.filter(data => {
    const groupMatch = selectedGroup === 'all' || data.team.group_number === parseInt(selectedGroup);
    const searchMatch = !searchTeam || (data.team.name || '').toLowerCase().includes(searchTeam.toLowerCase());
    return groupMatch && searchMatch;
  });

  // Trier par nombre de cartons (plus de cartons en premier)
  const sortedData = [...filteredData].sort((a, b) => {
    const totalA = a.totalYellow + (a.totalRed * 3);
    const totalB = b.totalYellow + (b.totalRed * 3);
    return totalB - totalA;
  });

  // Équipes avec suspensions
  const teamsWithSuspensions = sortedData.filter(data => data.suspensions > 0);

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 style={styles.title}>🟨🟥 {t('discipline')}</h1>

      <div style={styles.infoBox}>
        <p>{language === 'ar'
          ? '💡 القواعد: 3 بطاقات صفراء = إيقاف مباراة واحدة | 1 بطاقة حمراء = إيقاف مباراة واحدة'
          : '💡 Règles: 3 cartons jaunes = 1 match de suspension | 1 carton rouge = 1 match de suspension'}</p>
      </div>

      {/* Filtres */}
      <div style={styles.filtersCard}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Groupe:</label>
          <select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">{language === 'ar' ? 'كل المجموعات' : 'Tous les groupes'}</option>
            {[1, 2, 3, 4, 5, 6].map(g => (
              <option key={g} value={g}>{language === 'ar' ? 'المجموعة' : 'Groupe'} {g}</option>
            ))}
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>{language === 'ar' ? 'بحث عن فريق' : 'Recherche équipe'}:</label>
          <input
            type="text"
            placeholder={language === 'ar' ? 'اسم الفريق...' : 'Nom de l\'équipe...'}
            value={searchTeam}
            onChange={(e) => setSearchTeam(e.target.value)}
            style={styles.filterSelect}
          />
        </div>
      </div>

      {/* Équipes avec suspensions */}
      {teamsWithSuspensions.length > 0 && (
        <div style={styles.suspensionsCard}>
          <h2 style={styles.cardTitle}>
            <FaExclamationTriangle style={styles.warningIcon} />
            Équipes avec Suspensions ({teamsWithSuspensions.length})
          </h2>

          <div style={styles.suspensionsList}>
            {teamsWithSuspensions.map(data => (
              <div key={data.team.id} style={styles.suspensionItem}>
                <div style={styles.teamInfo}>
                  {data.team.logo_url && (
                    <img src={data.team.logo_url} alt="" style={styles.teamLogo} onError={(e) => e.target.style.display = 'none'} />
                  )}
                  <div>
                    <div style={styles.teamName}>{data.team.name}</div>
                    <div style={styles.teamGroup}>Groupe {data.team.group_number}</div>
                  </div>
                </div>
                <div style={styles.suspensionBadge}>
                  {data.suspensions} match{data.suspensions > 1 ? 's' : ''} de suspension
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tableau des cartons */}
      <div style={styles.cardsCard}>
        <h2 style={styles.cardTitle}>
          <FaHistory style={styles.icon} />
          Statistiques des Cartons ({sortedData.length} équipes)
        </h2>

        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}>Équipe</th>
                <th style={styles.th}>Groupe</th>
                <th style={styles.th}>🟨 Jaunes (Total)</th>
                <th style={styles.th}>🟥 Rouges</th>
                <th style={styles.th}>🟨 Restants</th>
                <th style={styles.th}>⚠️ Suspensions</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map(data => (
                <tr key={data.team.id} style={styles.tableRow}>
                  <td style={styles.td}>
                    <div style={styles.teamCell}>
                      {data.team.logo_url && (
                        <img src={data.team.logo_url} alt="" style={styles.smallLogo} onError={(e) => e.target.style.display = 'none'} />
                      )}
                      <span>{data.team.name}</span>
                    </div>
                  </td>
                  <td style={styles.td}>{data.team.group_number}</td>
                  <td style={styles.td}>
                    <span style={styles.yellowBadge}>{data.totalYellow}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.redBadge}>{data.totalRed}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.yellowBadge, opacity: 0.7 }}>{data.yellowCards}/3</span>
                  </td>
                  <td style={styles.td}>
                    {data.suspensions > 0 ? (
                      <span style={styles.suspensionCount}>{data.suspensions}</span>
                    ) : (
                      <span style={styles.noSuspension}>-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Statistiques globales */}
      <div style={styles.statsCard}>
        <h2 style={styles.cardTitle}>📊 Statistiques Globales</h2>
        <div style={styles.statsGrid}>
          <div style={styles.statBox}>
            <div style={styles.statValue}>{sortedData.reduce((sum, d) => sum + d.totalYellow, 0)}</div>
            <div style={styles.statLabel}>🟨 Total Cartons Jaunes</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statValue}>{sortedData.reduce((sum, d) => sum + d.totalRed, 0)}</div>
            <div style={styles.statLabel}>🟥 Total Cartons Rouges</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statValue}>{sortedData.reduce((sum, d) => sum + d.suspensions, 0)}</div>
            <div style={styles.statLabel}>⚠️ Total Suspensions</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statValue}>{teamsWithSuspensions.length}</div>
            <div style={styles.statLabel}>🚫 Équipes Concernées</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  title: { fontSize: '32px', fontWeight: 'bold', marginBottom: '20px' },
  infoBox: { background: '#fff3cd', border: '2px solid #ffc107', borderRadius: '10px', padding: '15px 20px', marginBottom: '30px', color: '#856404' },
  filtersCard: { background: 'white', padding: '20px', borderRadius: '15px', marginBottom: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
  filterGroup: { display: 'flex', alignItems: 'center', gap: '10px' },
  filterLabel: { fontSize: '14px', fontWeight: 'bold', color: '#666' },
  filterSelect: { padding: '8px 15px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', fontWeight: '600', flex: 1, maxWidth: '300px' },
  suspensionsCard: { background: '#fff3cd', padding: '25px', borderRadius: '15px', marginBottom: '30px', border: '3px solid #ffc107' },
  cardTitle: { fontSize: '22px', fontWeight: 'bold', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' },
  warningIcon: { color: '#ff6b6b', fontSize: '24px' },
  icon: { color: '#667eea' },
  suspensionsList: { display: 'flex', flexDirection: 'column', gap: '15px' },
  suspensionItem: { background: 'white', padding: '20px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  teamInfo: { display: 'flex', alignItems: 'center', gap: '15px' },
  teamLogo: { width: '50px', height: '50px', objectFit: 'contain' },
  teamName: { fontSize: '18px', fontWeight: 'bold', color: '#333' },
  teamGroup: { fontSize: '13px', color: '#666', marginTop: '3px' },
  suspensionBadge: { padding: '10px 20px', background: '#ff6b6b', color: 'white', borderRadius: '20px', fontWeight: 'bold', fontSize: '14px' },
  cardsCard: { background: 'white', padding: '25px', borderRadius: '15px', marginBottom: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
  tableContainer: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { background: '#f5f5f5' },
  th: { padding: '15px', textAlign: 'left', fontWeight: 'bold', color: '#333', borderBottom: '2px solid #e0e0e0' },
  tableRow: { borderBottom: '1px solid #f0f0f0', transition: 'background 0.2s' },
  td: { padding: '15px', color: '#555' },
  teamCell: { display: 'flex', alignItems: 'center', gap: '10px' },
  smallLogo: { width: '30px', height: '30px', objectFit: 'contain' },
  yellowBadge: { padding: '5px 12px', background: '#fff3cd', color: '#856404', borderRadius: '12px', fontWeight: 'bold', fontSize: '13px' },
  redBadge: { padding: '5px 12px', background: '#f8d7da', color: '#721c24', borderRadius: '12px', fontWeight: 'bold', fontSize: '13px' },
  suspensionCount: { padding: '5px 12px', background: '#ff6b6b', color: 'white', borderRadius: '12px', fontWeight: 'bold', fontSize: '13px' },
  noSuspension: { color: '#ccc', fontSize: '18px' },
  statsCard: { background: 'white', padding: '25px', borderRadius: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' },
  statBox: { background: '#f9f9f9', padding: '25px', borderRadius: '12px', textAlign: 'center' },
  statValue: { fontSize: '36px', fontWeight: 'bold', color: '#667eea', marginBottom: '10px' },
  statLabel: { fontSize: '14px', color: '#666', fontWeight: '500' },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#666' },
  spinner: { width: '50px', height: '50px', border: '5px solid #f3f3f3', borderTop: '5px solid #c41e3a', borderRadius: '50%', animation: 'spin 1s linear infinite' }
};

// Animation CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  tr:hover {
    background: #f9f9f9 !important;
  }
`;
if (!document.getElementById('discipline-section-styles')) {
  styleSheet.id = 'discipline-section-styles';
  document.head.appendChild(styleSheet);
}

export default DisciplineSection;
